import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faPalette } from '@fortawesome/pro-light-svg-icons/faPalette';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { catalogArtworkSrc } from '../catalog/catalogArtwork';
import { presaveCreateMessages } from './presaveCreateMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './PresaveCreate.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type PresaveSongPill = 'upcoming' | 'live' | 'out';

interface PresaveSongCardProps {
  item: CatalogItem;
  pill: PresaveSongPill;
  /** The formatted release day, or undefined for none. */
  dayText?: string;
  /** Set where the artist may change the day (a1); the editor itself is `dateEditor`. */
  onChangeDay?: () => void;
  /** The date editor of a3, under the facts. */
  dateEditor?: React.ReactNode;
  /** Set where the cover editor is offered (not in a native web view). */
  onEditCover?: () => void;
  onUploadCover?: () => void;
  onEditSong?: () => void;
  /** Extra fact rows (the created screen shows the services). */
  extraFacts?: { label: string; value: string }[];
}

/**
 * The song a pre-save is made from (design presave-create a1-a3, b1): cover, title, artist and the
 * facts the page takes. Every change goes through the song's own editors.
 */
const PresaveSongCardComponent: React.FC<PresaveSongCardProps> = ({
  item,
  pill,
  dayText,
  onChangeDay,
  dateEditor,
  onEditCover,
  onUploadCover,
  onEditSong,
  extraFacts,
}) => {
  const intl = useIntl();
  const cover = catalogArtworkSrc(item, 96);
  const pillText = {
    upcoming: intl.formatMessage(presaveCreateMessages.pillUpcoming),
    live: intl.formatMessage(presaveCreateMessages.pillLive),
    out: intl.formatMessage(presaveCreateMessages.pillOut),
  }[pill];

  return (
    <aside className={styles.card} data-testid="presave-song-card">
      <div className={styles.cardHead}>
        <span>{intl.formatMessage(presaveCreateMessages.cardHead)}</span>
        <span className={clsx(styles.pill, pill === 'upcoming' ? styles.pillUp : styles.pillOut)}>{pillText}</span>
      </div>
      {cover === undefined ? (
        <div className={clsx(styles.art, styles.artEmpty)}>{intl.formatMessage(presaveCreateMessages.noCover)}</div>
      ) : (
        <div className={styles.art}>
          <img src={cover} alt="" />
        </div>
      )}
      {cover === undefined && (onEditCover !== undefined || onUploadCover !== undefined) && (
        <div className={styles.artActions}>
          {onEditCover !== undefined && (
            <SMLButton size="sm" color="primary" omitCap leftIcon={faPalette} onClick={onEditCover}>
              {intl.formatMessage(presaveCreateMessages.designCover)}
            </SMLButton>
          )}
          {onUploadCover !== undefined && (
            <SMLButton size="sm" outline omitCap onClick={onUploadCover}>
              {intl.formatMessage(presaveCreateMessages.uploadCover)}
            </SMLButton>
          )}
        </div>
      )}
      <div className={styles.cardIdentity}>
        <div className={styles.cardTitle}>{item.title}</div>
        <div className={styles.cardArtist}>{item.artistName}</div>
      </div>
      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>{intl.formatMessage(presaveCreateMessages.factReleaseDay)}</dt>
          <dd>
            <span className={dayText === undefined ? styles.factNone : styles.factValue}>
              {dayText ?? intl.formatMessage(presaveCreateMessages.factNoDay)}
            </span>
            {onChangeDay !== undefined && (
              <button type="button" className={styles.link} onClick={onChangeDay}>
                {intl.formatMessage(presaveCreateMessages.change)}
              </button>
            )}
          </dd>
        </div>
        <div className={styles.fact}>
          <dt>{intl.formatMessage(presaveCreateMessages.factCover)}</dt>
          <dd>
            <span className={cover === undefined ? styles.factNone : styles.factValue}>
              {intl.formatMessage(
                cover === undefined ? presaveCreateMessages.noCover : presaveCreateMessages.factCoverReady
              )}
            </span>
            {cover !== undefined && onEditCover !== undefined && (
              <button type="button" className={styles.link} onClick={onEditCover}>
                {intl.formatMessage(presaveCreateMessages.editCover)}
              </button>
            )}
          </dd>
        </div>
        {item.releaseType !== null && item.releaseType !== '' && (
          <div className={styles.fact}>
            <dt>{intl.formatMessage(presaveCreateMessages.factType)}</dt>
            <dd>
              <span className={styles.factValue}>{item.releaseType}</span>
            </dd>
          </div>
        )}
        {extraFacts?.map((fact) => (
          <div key={fact.label} className={styles.fact}>
            <dt>{fact.label}</dt>
            <dd>
              <span className={styles.factValue}>{fact.value}</span>
            </dd>
          </div>
        ))}
      </dl>
      {dateEditor}
      {onEditSong !== undefined && (
        <div className={styles.cardFoot}>
          <FontAwesomeIcon icon={faCheck} className={styles.footIcon} />
          <span>
            {intl.formatMessage(presaveCreateMessages.cardFoot)}{' '}
            <button type="button" className={styles.link} onClick={onEditSong}>
              {intl.formatMessage(presaveCreateMessages.editSong)}
            </button>
          </span>
        </div>
      )}
    </aside>
  );
};

export const PresaveSongCard = TypedMemo(PresaveSongCardComponent);
